import React, { useMemo, useState } from 'react'
import { trans } from 'sharedKernel/i18n'
import { removeTagsHTML, sortByAvailability } from 'sharedKernel/handlers'
import { NoContent } from 'components/NoContent'
import { Loading } from 'components/loading'
import { Filter } from 'components/Filter'
import { PageDescription, PageTitle } from 'domain/feature/hub/pageTitle/styles'
import { useGetScoutingCriteria } from 'domain/feature/hub/company/scouting/api/scoutingService'
import NoContentImg from 'assets/images/defaults/no_ideas.png'
import { formatOpportunityVerticalsDisplay } from 'domain/feature/hub/company/scouting/scoutingOpportunityDisplay'
import { useGetResolverOpportunities } from './api'
import { isOpportunityClosed } from './behavior'
import { OpportunityCardItem } from './components/OpportunityCardItem'
import {
  EmptySearchMessage,
  OpportunityList,
  PageHeaderWrapper,
  PageWrapper,
  MobileContainerPadding,
  OpportunityListGlobalStyle,
  SearchSection,
} from './style'
import { formatOpportunityCriteriaText } from './opportunityCriteriaText'

export const HubStartupOpportunityFeature = (): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState('')
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('')

  const { data: opportunitiesData, isFetching } = useGetResolverOpportunities()
  const { data: criteriaDefinitions } = useGetScoutingCriteria()

  const criteriaDefinitionsRows = criteriaDefinitions?.rows
  const rows = opportunitiesData?.rows ?? []

  const filteredRows = useMemo(() => {
    const term = appliedSearchTerm.trim().toLowerCase()
    const filtered = !term
      ? rows
      : rows.filter((opportunity) => {
          const criteriaText = formatOpportunityCriteriaText(opportunity, criteriaDefinitionsRows)
          const searchableFields = [
            opportunity.title,
            removeTagsHTML(opportunity.description ?? ''),
            opportunity.requesterName,
            opportunity.institutionName,
            opportunity.operationArea,
            formatOpportunityVerticalsDisplay(opportunity),
            opportunity.maxValue,
            criteriaText,
          ]

          return searchableFields.some((field) =>
            String(field ?? '').toLowerCase().includes(term)
          )
        })

    return sortByAvailability(
      filtered,
      isOpportunityClosed,
      (opportunity) => opportunity.interestDeadline
    )
  }, [appliedSearchTerm, rows, criteriaDefinitionsRows])

  if (isFetching) return <Loading />

  return (
    <>
      <MobileContainerPadding />
      <OpportunityListGlobalStyle />
      <PageWrapper className="hub-startup-opportunity-list-root">
        <PageHeaderWrapper>
          <PageTitle>{trans('scouting.opportunity.startup.pageTitle')}</PageTitle>
          <PageDescription style={{ marginBottom: 0 }}>
            {trans('scouting.opportunity.startup.pageDescription')}
          </PageDescription>
        </PageHeaderWrapper>

        <SearchSection>
          <Filter
            currentSearchTerm={searchTerm}
            btnClick={setAppliedSearchTerm}
            onFilterChange={(term: string) => {
              setSearchTerm(term)
              if (term === '') setAppliedSearchTerm('')
            }}
            minSearchTermLength={1}
            searchPlaceholder={trans('scouting.opportunity.list.searchPlaceholder')}
          />
        </SearchSection>

        {rows.length === 0 ? (
          <NoContent text={trans('scouting.opportunity.startup.emptyList')} image={NoContentImg} />
        ) : filteredRows.length === 0 ? (
          <EmptySearchMessage>
            {trans('scouting.opportunity.list.noSearchResults')}
          </EmptySearchMessage>
        ) : (
          <OpportunityList>
            {filteredRows.map((opportunity) => (
              <OpportunityCardItem key={opportunity.id} opportunity={opportunity} />
            ))}
          </OpportunityList>
        )}
      </PageWrapper>
    </>
  )
}
